<template>
<div class="chapter">
<div class="inner_page">
<div class="section">
<div class="header">
<div class="hgroup">
<h3></h3>
<h1>Chapter 3</h1>
</div>
</div>
<p class="first">
<span style="font-size: 1em; line-height: 1.5; text-indent: 1.5em;">Quinn’s room in the women’s dormitory was moderately neat, due to the day’s cleaning binge. Each month, exactly twenty-four hours before her period arrived, Quinn became ferociously tidy. She folded clothes, straightened drawers, sorted socks, dusted surfaces with maniacal energy. The urge disappeared the following day, not to return for another four weeks. By the time she was fifteen, she had learned to take full advantage of her compulsion or the piles of books, clothes, souvenirs, and half-eaten Hostess Twinkies would collect underfoot until the next time around.</span></p>
<p class="indent">
	She had livened up her cubicle with warm colors—a bright patchwork quilt on the narrow bed, a secondhand rocking chair that she had fitted with yellow pillows, and beside the chair a straw basket that held three giant paper flowers—red, yellow, and orange. On the linoleum floor were three bath mats from Woolworth’s bargain table—again, red, yellow, and orange. The walls were decorated with posters: John Kennedy barefoot on a Cape Cod beach; Ike and Tina Turner in concert; and a travel poster of County Kerry, Ireland. All were unframed but carefully attached with hidden circles of masking tape. At the bottom left-hand corner of the Kennedy poster drooped a wilted white carnation, taped there to commemorate the first anniversary of his death.</p>
<p class="indent">
	Quinn sat at her desk by the window, ostensibly committing to memory the postulations of <em>Totem and Taboo.</em> Her right foot tapped rhythmically against the floor as she absorbed the marked-up pages.</p>
<p class="indent">
	Perched precariously on the edge of the desk was a portrait of her parents. Their features, fuzzy and idealized, faded into one another with varying shades of beige—except for the eyes, both pairs identically blue there, although, of course, her father’s were actually hazel. Quinn had gazed at the picture so often she imagined she had blurred the outline of their faces by staring at them so much.</p>
<p class="indent">
	Her yellow Magic Marker squeaked as she highlighted another paragraph. All but five sentences were illuminated with the bold transparent track. Sighing, she tossed the book on the desk and stretched. Her eyes shifted to the drawer. She stared at it and then, after a quick glance over her shoulder, opened it cautiously. She removed an envelope, extracted from it two type-written sheets that were stapled together, and began to read.</p>
<p class="indent">
	Quinn was halfway down the first page when Van entered the room. She approached the desk unnoticed and reached down curiously to examine the papers that appeared so absorbing. Quinn jumped up with an exclamation, stuffed the pages into the envelope, and held it behind her back.</p>
<p class="indent">
	“Excuse me. I didn’t mean to startle you,” Van said. “What’s that?”</p>
<p class="indent">
	Quinn’s face had begun to redden, but she summoned enough composure to slide the envelope back into her desk. “Nothing,” she said casually, closing the drawer. “You just surprised me, that’s all.”</p>
<p class="indent">
	“Letter from home?” Van pressed.</p>
<p class="indent">
	“Yeah.”</p>
<p class="indent">
	Van peered closely into the flushed face. “I don’t believe you.”</p>
<p class="indent">
	Quinn watched Van’s eyes fix on the drawer.</p>
<p class="indent">
	“You wouldn’t,” Quinn said.</p>
<p class="indent">
	Van’s body was stiff and she held her breath.</p>
<p class="indent">
	“You’re much too inhibited, Vanessa.”</p>
<p class="indent">
	Van lunged for the drawer and yanked it open. Quinn yelped and grabbed at it, but Van had got there first. She backed away, holding the letter above her head. Quinn stretched desperately, but Van was just tall enough. She waved the envelope back and forth out of reach.</p>
<p class="indent">
	“I don’t believe you did that. I’ll never trust you again,” Quinn protested. “It’s a federal offense, interfering with the mail.”</p>
<p class="indent">
	“I’m not going to read it. I just want to see who it’s from.” Van peered at the return address. “Chris Hartley? Hey, is this what the recent mailbox obsession is all about?”</p>
<p class="indent">
	Quinn slumped down at her desk, defeated.</p>
<p class="indent">
	“I want to read it. May I?”</p>
<p class="indent">
	Quinn looked at her balefully, then shrugged. Van began to skim the pages. She made no comment, only raised her eyes once to glance at her friend’s defiant face. When she had finished, she sat still for a moment, then said, “Are you going to report this?”</p>
<p class="indent">
	“What for?”</p>
<p class="indent">
	Van dangled the letter gingerly between two fingers as if it were on fire. “This is one sick boy.”</p>
<p class="indent">
	“Oh, that’s not his own stuff. He copied it all from secondary sources.”</p>
<p class="indent">
	“Which, an Abnormal Psych textbook or The College Man’s Rape Manual?”</p>
<p class="indent">
	“Mostly the <em>Kama Sutra,</em> I think,” Quinn said.</p>
<p class="indent">
	Van sat down on the bed. “You mind telling me what’s going on?”</p>
<p class="indent">
	“Yeah, I do. But since you bullied your way into it, I guess I might as well. That letter was commissioned.”</p>
<p class="indent">
	“Commissioned,” Van repeated dully.</p>
<p class="indent">
	“Look, I’m going to be twenty-one years old in a few weeks and I’m probably the only virgin in the senior class.”</p>
<p class="indent">
	“That may well be true.”</p>
<p class="indent">
	“I can’t <em>graduate</em> like this.”</p>
<p class="indent">
	“What would people think?” Van said.</p>
<p class="indent">
	Quinn continued, her voice extravagantly patient, as if she were talking to a feebleminded child. “I don’t want just <em>anybody</em> to do it, do I? It has to be the right person.”</p>
<p class="indent">
	“Chris Hartley is applying for the position of your deflowerer?”</p>
<p class="indent">
	Quinn nodded.</p>
<p class="indent">
	“You’re crazy,” Van said.</p>
<p class="indent">
	“I knew you’d say that, which is exactly why I didn’t tell you. And if you mention this to a living soul I will personally remove your toenails.”</p>
<p class="indent">
	Van shook her head. “What’re the conditions of this contest, or whatever it is?”</p>
<p class="indent">
	“Well, the rules called for something original,” Quinn answered. “I hope the other guys read the instructions at least.”</p>
<p class="indent">
	“Just how many <em>are</em> there?”</p>
<p class="indent">
	Quinn reached for her Religion notebook and flipped it open to the last lecture’s notes. Silently she pointed to the list she had penciled in the margin. The names and their descriptions had been heavily embellished with doodles, but Van could still make them out.</p>
<blockquote>
<p class="first">
<span class="small">CHRIS H</span>.: maternal instinct</p>
<p class="first">
<span class="small">JERRY L</span>.: body beautiful</p>
<p class="first">
<span class="small">PHIL S</span>.: wiseass</p>
<p class="first">
<span class="small">MYRON S</span>.: intellect</p>
<p class="first">
<span class="small">JACK W</span>.: good jokes</p>
<p class="first">
<span class="small">BOB K</span>.: gentle soul</p>
</blockquote>
<p class="indent">
	Van looked at Quinn in silence for a moment. Then she said, “Did you ever consider availing yourself of the free student-counseling service?”</p>
<p class="indent">
	“Are you kidding? What would I do with a shrink?”</p>
<p class="indent">
	“It might help.”</p>
<p class="indent">
	“Help what? I don’t need help. I need Marvin the Magnificent. I’m going to get him.”</p>
<p class="indent">
	“But Quinn …” Van’s forehead was wrinkled with the effort to explain. “It’s kind of … bizarre, don’t you think? To go about it this way?”</p>
<p class="indent">
	“I think it’s eminently practical.”</p>
<p class="indent">
	“I wish you all the luck in the world.”</p>
<p class="indent">
	“You have no faith.”</p>
<p class="indent">
	“Can’t you see? It’s like … coupling by computer. Mail-order sex. You’ve got this thing about control, and it isn’t something you <em>can</em> control. Or ought to, anyway. People fall in love by accident.”</p>
<p class="indent">
	“I don’t.”</p>
<p class="indent">
	“I really get the feeling you’re involved in a classical search for the ideal father figure.”</p>
<p class="indent">
	“Oh, <em>can</em> it, Freud, for God’s sake. Quit analyzing me.</p>
<p class="indent">
	Van fell silent.</p>
<p class="indent">
	“Until you butted into this I was having a lot of fun,” Quinn said. Van looked so pained that Quinn’s face softened and she held her hand out placatingly. “Hey, listen, it’s just that it’s time for me now. It’d be okay if we could put Stanley through the mimeograph machine, but there’s no way. Can’t I have someone, too?”</p>
<p class="indent">
	Van held her hair coiled into a twisted mass on the top of her head. Now she sighed and released it, letting it fall silky and dark past her shoulders. She stood up, headed slowly for the door, and turned to look at Quinn. “Listen, you’ll keep me posted?”</p>
<p class="indent">
	Quinn nodded.</p>
<p class="indent">
	Van hesitated for a moment, then said good night and closed the door carefully behind her.</p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 009_Chapter_03.vue?7ed94d54